import React, { useCallback } from 'react';
import { UseControllerProps, UseControllerReturn } from 'react-hook-form';
import { addModifiers } from '../../../../shared/helpers';
import { RangeFieldTransformer } from '../FieldTransformers/FieldTransformers';
import flow from 'lodash/flow';

import './FilterFields.scss';
import { InputField } from './InputField';
import { getInputId } from './getInputId';

export type RangeController = {
  min: UseControllerReturn;
  max: UseControllerReturn;
};

interface Props {
  title: string;
  controllerProps: UseControllerProps;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  type?: 'number' | 'percent';
  min?: number;
  max?: number;
  transformInput?: (value: any) => string;
  transformOutput?: (value: string) => any;
}

const transformStub = (value: any) => value;

export const transformPercentInput = (value: any) => {
  if (Number(value)) {
    return String(Math.floor(Number(value) * 100)); //Math.floor is needed because of rounding errors
  }
  return value;
};

export const transformPercentOutput = (value: any) => {
  if (Number(value)) {
    return String(Number(value) / 100);
  }
  return value;
};

export const RangeField = ({
  title,
  controllerProps,
  className,
  size = 'small',
  type = 'number',
  min,
  max,
  transformInput: transformInputParent = transformStub,
  transformOutput: transformOutputParent = transformStub,
}: Props) => {
  const inputClassName = addModifiers('form__input', [size]);
  const nameKeys = new RangeFieldTransformer(controllerProps.name).getOutputKeys();

  const transformOutput = useCallback(
    (value: any) =>
      flow((value: string) => {
        if (typeof min === 'number' && Number(value) < min) {
          return min;
        }

        if (typeof max === 'number' && Number(value) > max) {
          return max;
        }

        if (value.length > 1 && value[0] === '0') {
          return value.slice(1);
        }
        return value;
      }, transformOutputParent)(value),
    [min, max, transformOutputParent]
  );

  return (
    <div className={[className, 'range-field'].join(' ')}>
      <label htmlFor={getInputId(nameKeys.min)} className="range-field__title">
        {title}
        {type === 'percent' && ', %'}
      </label>
      <div className="range-field__inputs">
        <InputField
          inputProps={{
            placeholder: min,
            className: inputClassName,
            type: 'number',
          }}
          controllerProps={{ ...controllerProps, name: nameKeys.min }}
          transformOutput={transformOutput}
          transformInput={transformInputParent}
        ></InputField>
        <label htmlFor={getInputId(nameKeys.max)} className="range-field__label">
          to{' '}
        </label>
        <InputField
          controllerProps={{ ...controllerProps, name: nameKeys.max }}
          inputProps={{
            placeholder: max,
            className: inputClassName,
            type: 'number',
          }}
          transformOutput={transformOutput}
          transformInput={transformInputParent}
        ></InputField>
      </div>
    </div>
  );
};
